import type {FullInfo} from '@rocket-ui/lk-api';
import {useFetchProfile} from '@rocket-ui/lk-api';
import {
	formatDateDisplay,
	formatDepartmentCodeDisplay,
	formatPassportSeriesNumberDisplay,
} from 'features/registration/model/passport';

export type ProfileDocumentIcon = 'passport' | 'inn';

export type ProfileDocumentItem = {
	id: 'passport' | 'inn';
	title: string;
	value: string;
	icon: ProfileDocumentIcon;
};

export type ProfilePassportPhoto = {
	id: string;
	url: string;
	label: string;
};

export type ProfilePassportDetails = {
	seriesNumber: string;
	issuer: string;
	issuedAt: string;
	departmentCode: string;
	birthDate: string;
	birthPlace: string;
	photos: ProfilePassportPhoto[];
};

type IdentificationDocumentLike = {
	series?: string | null;
	number?: string | null;
	issuer?: string | null;
	issuedAt?: string | null;
	departmentCode?: string | null;
	birthDate?: string | null;
	birthPlace?: string | null;
	photos?: {
		passportPhoto?: string | null;
		passportRegistrationPhoto?: string | null;
		passportSelfie?: string | null;
		residenceDocument?: string | null;
		residentEntryDocument?: string | null;
		residentDepartureDocument?: string | null;
	} | null;
};

function asIdentificationDocument(value: unknown): IdentificationDocumentLike | null {
	if (!value || typeof value !== 'object') {
		return null;
	}

	return value as IdentificationDocumentLike;
}

function formatProfileText(value: string | null | undefined): string {
	const trimmed = value?.trim() ?? '';
	return trimmed || '—';
}

/** ISO / произвольная дата → «ДД.ММ.ГГГГ». */
export function formatProfileDocumentDate(value: string | null | undefined): string {
	const raw = value?.trim() ?? '';

	if (!raw) {
		return '—';
	}

	const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);

	if (isoMatch) {
		const [, year, month, day] = isoMatch;
		return `${day}.${month}.${year}`;
	}

	const display = formatDateDisplay(raw);
	return display || '—';
}

export function formatProfilePassportNumber(document: IdentificationDocumentLike | null): string {
	const series = document?.series?.replace(/\D/g, '') ?? '';
	const number = document?.number?.replace(/\D/g, '') ?? '';
	const combined = `${series}${number}`;

	if (!combined) {
		return '—';
	}

	return formatPassportSeriesNumberDisplay(combined) || '—';
}

export function formatProfileInn(inn: string | null | undefined): string {
	const digits = (inn ?? '').replace(/\D/g, '');

	return digits || '—';
}

function mapPassportPhotos(document: IdentificationDocumentLike | null): ProfilePassportPhoto[] {
	const photos = document?.photos;

	if (!photos) {
		return [];
	}

	const entries: {id: string; url: string | null | undefined; label: string}[] = [
		{id: 'passportPhoto', url: photos.passportPhoto, label: 'Разворот'},
		{id: 'passportRegistrationPhoto', url: photos.passportRegistrationPhoto, label: 'Прописка'},
		{id: 'passportSelfie', url: photos.passportSelfie, label: 'Селфи с паспортом'},
		{id: 'residenceDocument', url: photos.residenceDocument, label: 'Проживание'},
		{id: 'residentEntryDocument', url: photos.residentEntryDocument, label: 'Въезд'},
		{id: 'residentDepartureDocument', url: photos.residentDepartureDocument, label: 'Выезд'},
	];

	return entries
		.filter((entry) => Boolean(entry.url?.trim()))
		.map((entry) => ({
			id: entry.id,
			url: entry.url!.trim(),
			label: entry.label,
		}));
}

export function mapFullInfoToPassportDetails(fullInfo: FullInfo | undefined): ProfilePassportDetails {
	const document = asIdentificationDocument(fullInfo?.identificationDocument);
	const departmentDigits = document?.departmentCode?.replace(/\D/g, '') ?? '';

	return {
		seriesNumber: formatProfilePassportNumber(document),
		issuer: formatProfileText(document?.issuer),
		issuedAt: formatProfileDocumentDate(document?.issuedAt),
		departmentCode: departmentDigits
			? formatDepartmentCodeDisplay(departmentDigits) || '—'
			: '—',
		birthDate: formatProfileDocumentDate(document?.birthDate),
		birthPlace: formatProfileText(document?.birthPlace),
		photos: mapPassportPhotos(document),
	};
}

export function mapFullInfoToProfileDocuments(fullInfo: FullInfo | undefined): ProfileDocumentItem[] {
	const document = asIdentificationDocument(fullInfo?.identificationDocument);
	const inn = fullInfo?.info?.inn;

	return [
		{
			id: 'passport',
			title: 'Паспорт',
			value: formatProfilePassportNumber(document),
			icon: 'passport',
		},
		{
			id: 'inn',
			title: 'Налоговый идентификатор',
			value: formatProfileInn(inn),
			icon: 'inn',
		},
	];
}

export function useProfileDocuments() {
	const query = useFetchProfile();

	return {
		documents: mapFullInfoToProfileDocuments(query.data),
		passport: mapFullInfoToPassportDetails(query.data),
		isLoading: query.isLoading,
		isError: query.isError,
		error: query.error,
	};
}
